/**
 * May the signed-in person do this act? — the database's answer, read once.
 *
 * Accepting a Catchball proposal and ratifying a Priority List each have a
 * `can_*` function (migration 987) that returns the same answer the act itself
 * acts on: today's rule, then the cycle's "Items / selection" decision right
 * when one is set. A page asks before it draws the control, and draws a plain
 * line instead when the answer is no (build contract §2.8). The act is still
 * the control: this only decides what the page shows.
 *
 * This file only reads the answer. The calls live with their acts:
 * `fetchAcceptRight` in lib/catchball/proposalDecisions.ts,
 * `fetchRatifyRight` in lib/priorityListRatification.ts,
 * `fetchPublishRight` in lib/sessionPublishRight.ts and
 * `fetchApproveRight` in lib/commands/approvePriorityList.ts (migration 1001).
 */

export interface ActRight {
  /** May do the act (for Catchball: the full, ratifying accept). */
  allowed: boolean;
  /** Catchball only: may accept provisionally (with conditions). Equals `allowed` elsewhere. */
  allowedProvisional: boolean;
  /** A decision right is set for this act on the cycle. */
  configured: boolean;
  /** Who holds the right, in words, when one is set. */
  heldBy: string | null;
  /** What the act would be allowed on (the governance event's actor_right_at_act). */
  basis: string | null;
  /** The plain sentence to show in place of the control; null when nothing is refused. */
  line: string | null;
}

/** PostgREST's "no such function": the database predates migration 987. */
const FUNCTION_NOT_FOUND = 'PGRST202';

function text(value: unknown): string | null {
  return typeof value === 'string' && value.trim() !== '' ? value.trim() : null;
}

/**
 * True when a decision right set for the act shuts this person out — the one
 * case a page leaves out a control it would otherwise draw by its own rule.
 * Rejecting or returning a proposal exercises the right accepting it does;
 * publishing a session's results and approving items onto the Priority List
 * exercise the right ratifying a list does (migration 1001). A right that is
 * not set (`configured` false) decides nothing: the page's own rule stands.
 */
export function refusedByRight(right: ActRight): boolean {
  return right.configured && !right.allowedProvisional;
}

/** Reads a `can_*` answer (`can_accept_catchball`, `can_ratify_priority_list`, `can_publish_session_outcome`, `can_approve_priority_list`). */
export function readActRight(data: unknown, fallbackLine: string): ActRight {
  const o = data && typeof data === 'object' && !Array.isArray(data) ? (data as Record<string, unknown>) : null;
  if (!o) {
    return { allowed: false, allowedProvisional: false, configured: false, heldBy: null, basis: null, line: fallbackLine };
  }
  const allowed = o.allowed === true;
  const allowedProvisional = typeof o.allowed_provisional === 'boolean' ? o.allowed_provisional : allowed;
  const refusedSomething = !allowed || !allowedProvisional;
  return {
    allowed,
    allowedProvisional,
    configured: o.configured === true,
    heldBy: text(o.held_by),
    basis: text(o.basis),
    line: refusedSomething ? text(o.reason) ?? (allowed ? null : fallbackLine) : null,
  };
}

/**
 * The answer when the check itself failed. A database without the `can_*`
 * function also has an act that does not check the right, so today's rule is
 * what the act would do and the control is drawn. Any other failure is not a
 * pass: the control is not drawn, and the line says why.
 */
export function actRightFromError(error: { code?: string | null; message?: string | null }, act: string): ActRight {
  if (error.code === FUNCTION_NOT_FOUND) {
    return { allowed: true, allowedProvisional: true, configured: false, heldBy: null, basis: null, line: null };
  }
  return {
    allowed: false,
    allowedProvisional: false,
    configured: false,
    heldBy: null,
    basis: null,
    line: `Could not check whether you may ${act}${error.message ? `: ${error.message}` : '.'}`,
  };
}
